import { useSyncExternalStore } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { TreeCacheScope } from 'api/decomposition-tree/types';
import { subscribeToTreeCacheScope } from 'api/decomposition-tree/utils/treeCacheScope';

import type { RichTreeItem } from '../../types';
import { isMoveNodeStale } from '../utils/isMoveNodeStale';

type UseIsMoveNodeStaleArgs = TreeCacheScope & {
  nodeDataNodeId: string;
  initialParentDataNodeId: string | null;
  liveTreeItems: RichTreeItem[];
  enabled?: boolean;
};

/** Recomputes stale state when this tree's react-query cache or live tree items change. */
export function useIsMoveNodeStale({
  projectId,
  templateNodeId,
  proposalId,
  nodeDataNodeId,
  initialParentDataNodeId,
  liveTreeItems,
  enabled = true
}: UseIsMoveNodeStaleArgs): boolean {
  const queryClient = useQueryClient();
  const scope = { projectId, templateNodeId, proposalId };

  return useSyncExternalStore(
    (onStoreChange) => {
      if (!enabled) return () => {};
      return subscribeToTreeCacheScope(queryClient, scope, onStoreChange);
    },
    () =>
      isMoveNodeStale(
        queryClient,
        projectId,
        templateNodeId,
        proposalId,
        nodeDataNodeId,
        initialParentDataNodeId,
        liveTreeItems
      ),
    () => false
  );
}
